import { Info } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AppHeader from "@/components/AppHeader";
import { useAuth } from "@/context/AuthContext";

/**
 * Records / ledger page.
 *
 * Historic build rendered fake `EMPLOYEES` / `ATTENDANCE` / `DEPLOYMENTS`
 * from /lib/dummyData.js — that fake data is NOT scoped to the caller's
 * location and would leak Prestige/Brigade/RMZ/etc. rows to every
 * Supervisor & Assistant Supervisor. Fix: remove the dummy render path
 * entirely and point the user at the real, server-side-scoped screens
 * that already exist. When we build a Records/Reports backend
 * (allocation review, attendance report + CSV export), this page will be
 * repointed at those endpoints.
 */
const ListView = () => {
  const navigate = useNavigate();
  const { isAdmin, isSupervisor, isAsstSupervisor } = useAuth();

  return (
    <div className="min-h-screen bg-vision-ice" data-testid="records-page">
      <AppHeader />

      <main className="mx-auto max-w-[1400px] px-6 py-8">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4 vision-rise">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.16em] text-vision-slate/60">
              Records
            </div>
            <h1 className="mt-1 font-display text-3xl font-extrabold text-vision-ink sm:text-4xl">
              Workforce &amp; deployment ledger
            </h1>
            <p className="mt-1 text-sm text-vision-slate/70">
              Central reports are coming soon. Use the shortcuts below for what&apos;s live today.
            </p>
          </div>
        </div>

        <div
          className="mb-6 flex items-start gap-3 rounded-2xl border bg-white p-5 shadow-card"
          style={{ borderColor: "rgba(52,73,94,0.08)" }}
          data-testid="records-empty-notice"
        >
          <Info
            className="mt-0.5 h-5 w-5 flex-none"
            style={{ color: "var(--vision-navy)" }}
          />
          <div>
            <div className="font-display text-lg font-bold text-vision-ink">
              Reports are on the way
            </div>
            <p className="mt-1 text-sm text-vision-slate/80">
              The consolidated Employees / Attendance / Deployments ledger will land with
              the Allocation Review + Attendance Export release. Until then, every list
              you see here would be un-scoped to your Location — so we&apos;ve turned it off
              rather than risk showing you data from a different site.
            </p>
          </div>
        </div>

        <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {(isSupervisor || isAsstSupervisor) && (
            <ShortcutCard
              testId="records-shortcut-attendance"
              title="Today's attendance"
              body="Start or resume the day's session, record scan-ins and scan-outs for your Location."
              cta="Go to Attendance"
              onClick={() => navigate("/attendance")}
            />
          )}
          {isAdmin && (
            <>
              <ShortcutCard
                testId="records-shortcut-employees"
                title="Employees master"
                body="Full employee directory, filterable by Location, Shift and Department."
                cta="Open Employees"
                onClick={() => navigate("/masters/employees")}
              />
              <ShortcutCard
                testId="records-shortcut-shifts"
                title="Shifts & Departments"
                body="Grace windows, capacity and every operational lookup used by attendance."
                cta="Open Masters"
                onClick={() => navigate("/masters")}
              />
            </>
          )}
          <ShortcutCard
            testId="records-shortcut-dashboard"
            title="Live dashboard"
            body="Present / Half-Day / Scanned-out counters refresh with your current scope."
            cta="Back to Dashboard"
            onClick={() => navigate("/dashboard")}
          />
        </section>
      </main>
    </div>
  );
};

const ShortcutCard = ({ testId, title, body, cta, onClick }) => (
  <div
    className="flex flex-col justify-between rounded-2xl border bg-white p-5 shadow-card"
    style={{ borderColor: "rgba(52,73,94,0.08)" }}
    data-testid={testId}
  >
    <div>
      <div className="font-display text-lg font-bold text-vision-ink">{title}</div>
      <p className="mt-1 text-sm text-vision-slate/80">{body}</p>
    </div>
    <button
      onClick={onClick}
      data-testid={`${testId}-btn`}
      className="mt-5 self-start btn-pill bg-[color:var(--vision-navy)] px-4 py-2 text-sm font-semibold text-white shadow-card hover:brightness-110"
    >
      {cta}
    </button>
  </div>
);

export default ListView;
